import { createRoot } from 'react-dom/client';

import i18n, { i18nReady } from './i18n';
import { ensureGuideTranslation } from './data/guide-translation-loader';
import { ALL_LANG_CODES, langHref } from './lib/i18n-routing';
import { registerServiceWorker } from './lib/register-sw';
import App from './App';

import './index.css';

// Preload the URL's language chunk before the first render so prerendered non-English
// pages don't flash English content while their locale chunk is still being fetched.
const base = import.meta.env.BASE_URL.replace(/\/$/, '');
const pathname = window.location.pathname;
const relPath = pathname.startsWith(base) ? pathname.slice(base.length) : pathname;
const seg = relPath.split('/').filter(Boolean)[0];
const urlLang =
  seg && (ALL_LANG_CODES as readonly string[]).includes(seg) ? seg : null;

// Legacy ?lng=XX query-string URLs (e.g. /mexc/?lng=pt) must be redirected to their
// path-based canonical form (/pt/mexc/) immediately — before the app renders — so
// Googlebot and users land on the correct URL and no redirect chain is formed.
// Removing lookupQuerystring alone doesn't stop crawlers following links that already
// exist in the wild, so we also need this client-side guard.
const qsLang = new URLSearchParams(window.location.search).get('lng');
const needsLngRedirect =
  !!qsLang &&
  (ALL_LANG_CODES as readonly string[]).includes(qsLang as (typeof ALL_LANG_CODES)[number]);

if (needsLngRedirect) {
  const langAgnosticPath = urlLang
    ? relPath.slice(`/${urlLang}`.length) || '/'
    : relPath || '/';
  const target = base + langHref(qsLang!, langAgnosticPath);
  // Replace history so the back-button doesn't return to the ?lng= URL.
  window.location.replace(target);
  // Do NOT fall through to bootstrap() — the page is navigating away.
} else {
  void bootstrap();
}

async function bootstrap() {
  // Fail open: if i18n init or the locale chunk fetch rejects, still render the app
  // (react-i18next falls back to keys/English) instead of leaving a blank page.
  try {
    await i18nReady;
    if (urlLang && i18n.language?.split('-')[0] !== urlLang) {
      await i18n.changeLanguage(urlLang);
    }
    // If the initial URL is a non-English guide article, preload its translation
    // chunk too, so the first client render doesn't flash English over the
    // prerendered localized HTML.
    if (urlLang) {
      const guideMatch = relPath.match(/\/guides\/([^/?#]+)/);
      if (guideMatch) {
        await ensureGuideTranslation(decodeURIComponent(guideMatch[1]).replace(/\/$/, ''), urlLang);
      }
    }
  } catch {
    // Swallow — render regardless of i18n readiness.
  }
  createRoot(document.getElementById('root')!).render(<App />);
  registerServiceWorker();
}
